import { BarChart3, FileText, Info, LogOut, LucideIcon, Menu, Moon, Sun, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { usePortalConfig } from '../hooks/usePortalConfig';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { PbfLogo } from './PbfLogo';
import { Button } from './ui/button';
import { Separator } from './ui/separator';

interface LayoutProps {
  children: React.ReactNode;
}

const ICON_MAP: Record<string, LucideIcon> = {
  BarChart3,
  FileText,
  Info,
};

export const Layout = ({ children }: LayoutProps) => {
  const { user, clearAuth } = useAuthStore();
  const { isDark, toggleTheme } = useThemeStore();
  const portalConfig = usePortalConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = portalConfig.pages
    .filter((p) => p.enabled)
    .map((p) => ({
      path: p.path,
      label: p.navLabel,
      Icon: (p.iconName ? ICON_MAP[p.iconName] : undefined) ?? BarChart3,
    }));

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const sidebarContent = (
    <>
      <div className="px-6 py-5">
        <PbfLogo className="h-9 w-auto" />
      </div>

      <Separator className="opacity-10" />

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map(({ path, label, Icon }) => {
          const isActive = location.pathname === path;
          return (
            <Link
              key={path}
              to={path}
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
              style={isActive ? { background: '#8C16C7', color: 'white' } : { color: 'rgba(255,255,255,0.55)' }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  (e.currentTarget as HTMLElement).style.color = 'white';
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.55)';
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }
              }}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </Link>
          );
        })}
      </nav>

      <Separator className="opacity-10" />

      <div className="p-4 space-y-3">
        <div>
          <div className="text-white/40 text-xs mb-0.5">Signed in as</div>
          <div className="text-white text-sm font-medium truncate">{user?.name || user?.email}</div>
          <div className="text-white/40 text-xs truncate">{user?.email}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 justify-start gap-2 text-white/50 hover:text-white hover:bg-white/10 h-8 px-2"
            onClick={handleLogout}
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/10"
            onClick={toggleTheme}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {isMobileMenuOpen && <div className="fixed inset-0 bg-black/60 z-20 lg:hidden" onClick={closeMobileMenu} />}

      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 flex flex-col flex-shrink-0 border-r border-white/10 transition-transform duration-200 lg:relative lg:translate-x-0 ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: '#2D2144' }}
      >
        <button className="lg:hidden absolute top-4 right-4 text-white/50 hover:text-white" onClick={closeMobileMenu}>
          <X className="w-5 h-5" />
        </button>
        {sidebarContent}
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div
          className="lg:hidden flex items-center gap-3 h-14 px-4 border-b border-white/10 flex-shrink-0"
          style={{ background: '#2D2144' }}
        >
          <button className="text-white/70 hover:text-white" onClick={() => setIsMobileMenuOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: '#8C16C7' }}>
              <BarChart3 className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-white font-semibold text-sm">Operator Portal</span>
          </div>
        </div>

        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
};
